import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import {
  Table, TR, TD, ColumnHeader
} from "psims/react/pages/primary-pages/data-submissions/shared/data-table-components";
import { AddButton, IconButton } from "psims/react/components/button";
import { EnforceableArrangement, FocusField, isDataFocusField } from "../types";
import useEnforceableArrangements from "./use-enforceable-arrangements";
import { UseFocusedField } from "psims/react/util/use-focused-field";
import { UseConfirm } from "../../../shared/use-confirm";
import * as XLSX from 'xlsx';
import Button from "psims/react/components/button/button";
import { BoxedSpan } from "psims/react/components/layout";
import Eraser from "psims/react/components/icons/eraser";
import FileExcel from "psims/react/components/icons/file-excel";
import ImportFile from "psims/react/components/icons/import-file";
import { borderStyle } from "psims/style/border";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  ctrl: ReturnType<typeof useEnforceableArrangements>;
  products: Array<{ id: number; name: string }>;
  forceErrors: boolean;   // ← add
  isDisabled: boolean;
  focusFieldCtrl: UseFocusedField<FocusField>;
  deleteCtrl: UseConfirm;
  msoCommentAnnualActivities: Array<{ commentCategoryType?: number; reporterComments?: string }>;
  onCommentChange: (comment: string) => void;
  caseId?: number | string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_COLUMNS = 9;

const AGREEMENT_TYPE_OPTIONS = [
  { value: 1, label: 'Entitled to take ownership (s23)' },
  { value: 2, label: 'Reserved or quarantined stock (s24)' },
];

// ─── Styled components ────────────────────────────────────────────────────────

const InfoBox = styled.div`
  background: #f5f7fa;
  border: 1px solid #d6dde6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;

  p { margin: 0; font-size: 14px; line-height: 1.5; }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  max-height: 600px;
  width: auto;

  table { border-collapse: collapse; }

  th {
    padding: 10px;
    height: 70px !important;
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f5f5f5;
    border-top: solid 1px silver;
  }

  td { padding: 4px 8px; }

  /* ── Native input/select reset ── */
  .cell-input,
  .cell-select {
    width: 100%;
    padding: 3px 6px;
    font-size: 14px;
    height: 28px;
    border: 1px solid #bbb;
    border-radius: 3px;
    background: #fff;
    box-sizing: border-box;
    font-family: inherit;
    outline: none;
    transition: border-color 0.15s;
  }

  .cell-input:focus,
  .cell-select:focus {
    border-color: #2684ff;
    box-shadow: 0 0 0 2px rgba(38, 132, 255, 0.2);
  }

  .cell-input:disabled,
  .cell-select:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }

  /* Select needs a touch more height for the arrow */
  .cell-select { height: 30px; }
  /* ── Validation error state ── */
  .cell-input--error {
    border-color: #d0021b !important;
    background-color: #fff8f8;
  }

  .cell-input--error:focus {
    border-color: #d0021b !important;
    box-shadow: 0 0 0 2px rgba(208, 2, 27, 0.2);
  }
    thead th {
    position: sticky;
    top: 0;
    background: #f5f5f5;
    border-top: solid 1px silver;
  }

  .sticky-col {
    position: sticky;
    left: 0;
    z-index: 4;
    background: white;
    box-shadow: 2px 0 0 #090909;
  }

  thead .sticky-col {
    z-index: 6;
    background: #f5f5f5;
  }
`;

// ─── Editor ───────────────────────────────────────────────────────────────────

//export to excel functionality


const EnforceableArrangementsEditor = ({ ctrl, products, forceErrors, isDisabled, focusFieldCtrl, deleteCtrl, msoCommentAnnualActivities, onCommentChange, caseId }: Props) => {
  const initialComment = msoCommentAnnualActivities
    ?.find(c => c.commentCategoryType === 10)
    ?.reporterComments ?? '';

  const [comment, setComment] = React.useState(initialComment);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
    onCommentChange(e.target.value);
  }, [onCommentChange]);

  const handleExport = useCallback(() => {
    const AGREEMENT_TYPE_LABELS: Record<number, string> = {
      1: 'Entitled to take ownership (s23)',
      2: 'Reserved or quarantined stock (s24)',
    };
    //export to excel functionality
    const rows = ctrl.rows.map(({ data }) => ({
      'Owner': data.ownerName ?? '',
      'Beneficiary': data.beneficiary ?? '',
      'MSO Product': products.find(p => p.id === data.msoProductId)?.name ?? '',
      'Agreement Type': data.stockOwnershipAgreementType
        ? AGREEMENT_TYPE_LABELS[data.stockOwnershipAgreementType] ?? ''
        : '',
      'Volume (ML)': data.volume ?? '',
      'Obligation Date': data.obligationDate ? new Date(data.obligationDate).toLocaleDateString('en-AU') : '',
      'Start Date': data.startDateOfThisArrangement ? new Date(data.startDateOfThisArrangement).toLocaleDateString('en-AU') : '',
      'End Date': data.endDateOfThisArrangement ? new Date(data.endDateOfThisArrangement).toLocaleDateString('en-AU') : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Enforceable Arrangements');

    // Optional: set column widths
    worksheet['!cols'] = [
      { wch: 30 }, // Owner
      { wch: 30 }, // Beneficiary
      { wch: 20 }, // MSO Product
      { wch: 40 }, // Agreement Type
      { wch: 14 }, // Volume
      { wch: 16 }, // Obligation Date
      { wch: 16 }, // Start Date
      { wch: 16 }, // End Date
    ];

    const fileName = caseId
      ? `${caseId}_Enforceable_Arrangements.xlsx`
      : 'Enforceable_Arrangements.xlsx';

    XLSX.writeFile(workbook, fileName);
  }, [ctrl.rows, products]);
  return (
    <div>
      <h2>Enforceable Arrangements</h2>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button  $kind="ghost"
          onClick={handleExport}
          disabled={ctrl.rows.length === 0}
        
         >
           <BoxedSpan box={{marginRight: 1}}><ImportFile size="sm"/></BoxedSpan> Export to excel
        </Button>
      </div>
      <InfoBox>
        <p>This page provides the information regarding enforceable arrangements your entity is required to fill out under 19F(2) of the <i>Petroleum and Other Fuels Reporting (POFR) Rules 2017</i>.</p>

      </InfoBox>

      <TableWrapper>
        <Table caption="">
          <thead>
            <TR>
              <ColumnHeader label="Owner" fixedWidth="260px" className="sticky-col" />
              <ColumnHeader label="Beneficiary" fixedWidth="280px" />
              <ColumnHeader label="MSO Product" fixedWidth="200px" />
              <ColumnHeader label="Agreement Type" fixedWidth="300px" />
              <ColumnHeader label="Volume (ML)" fixedWidth="100px" />
              <ColumnHeader label="Obligation Date" fixedWidth="140px" />
              <ColumnHeader label="Start Date" fixedWidth="140px" />
              <ColumnHeader label="End Date" fixedWidth="140px" />
              <ColumnHeader label="" fixedWidth="50px" />
            </TR>
          </thead>
          <tbody>
            {ctrl.rows.map((row, i) => (
              <TR key={row.data._key} $isOdd={i % 2 === 1}>
                <DataRow
                  row={row.data} ctrl={ctrl}
                  products={products}
                  forceErrors={forceErrors}
                  isDisabled={isDisabled}
                  validations={row.validations}
                  focusFieldCtrl={focusFieldCtrl}
                  deleteCtrl={deleteCtrl}
                />
              </TR>
            ))}
            {!isDisabled && (
              <TR>
                <TD colSpan={TOTAL_COLUMNS}>
                  <AddButton onClick={() => ctrl.add()} label="">
                    Add arrangement
                  </AddButton>
                </TD>
              </TR>
            )}
          </tbody>
        </Table>
      </TableWrapper>


      {/* Comments */}
      <div style={{ marginTop: 24 }}>
        <label
          htmlFor="ea_comment"
          style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}
        >
          Comments
        </label>
        <textarea
          id="ea_comment"
          disabled={isDisabled}
          value={comment}
          onChange={handleChange}
          maxLength={4000}
          rows={4}
          style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
        />
        <div style={{ textAlign: 'right', fontSize: '13px', color: '#666', marginTop: 4 }}>
          {comment.length} of 4000 characters used
        </div>
      </div>
    </div>
  );
};

// ─── DataRow ──────────────────────────────────────────────────────────────────

interface DataRowProps {
  row: EnforceableArrangement;
  ctrl: ReturnType<typeof useEnforceableArrangements>;
  products: Array<{ id: number; name: string }>;
  forceErrors: boolean;
  isDisabled: boolean;
  validations: Record<string, any>;
  focusFieldCtrl: UseFocusedField<FocusField>;
  deleteCtrl: UseConfirm;
}

const toDateValue = (val?: string | null) => {
  if (!val) return '';
  try { return new Date(val).toISOString().split('T')[0]; }
  catch { return ''; }
};

/**
 * Uses native inputs with defaultValue (uncontrolled) + onBlur flush.
 * This gives instant tab navigation and zero per-keystroke re-renders.
 */
const DataRow = ({ row, ctrl, products, forceErrors, isDisabled, validations, focusFieldCtrl, deleteCtrl }: DataRowProps) => {
  //console.log('DataRow render — validations:', validations, 'row:', row);
  const ownerNameRef = useRef<HTMLInputElement>(null);
  const beneficiaryRef = useRef<HTMLInputElement>(null);
  const msoProductIdRef = useRef<HTMLSelectElement>(null);
  const agreementTypeRef = useRef<HTMLSelectElement>(null);
  const volumeRef = useRef<HTMLInputElement>(null);
  const obligationDateRef = useRef<HTMLInputElement>(null);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  // 2. Watch focusedField and focus the matching input
  useEffect(() => {
    if (!isDataFocusField(focusFieldCtrl.focusedField)) return;
    const f = focusFieldCtrl.focusedField;
    if (f._id !== row._id) return;

    if (f.field === 'ownerName') ownerNameRef.current?.focus();
    if (f.field === 'beneficiary') beneficiaryRef.current?.focus();
    if (f.field === 'msoProductId') msoProductIdRef.current?.focus();
    if (f.field === 'stockOwnershipAgreementType') agreementTypeRef.current?.focus();
    if (f.field === 'volume') volumeRef.current?.focus();
    if (f.field === 'obligationDate') obligationDateRef.current?.focus();
    if (f.field === 'startDateOfThisArrangement') startDateRef.current?.focus();
    if (f.field === 'endDateOfThisArrangement') endDateRef.current?.focus();
  }, [focusFieldCtrl.focusedField, row._id]);

  const update = (field: keyof EnforceableArrangement, value: any) =>
    ctrl.update({ ...row, [field]: value });


  const [ownerName, setOwnerName] = useState(row.ownerName || '');
  const [beneficiary, setBeneficiary] = useState(row.beneficiary || '');
  const [msoProductId, setMsoProductId] = useState<number | ''>(row.msoProductId ?? '');
  const [stockOwnershipAgreementType, setStockOwnershipAgreementType] = useState<number | ''>(row.stockOwnershipAgreementType ?? '');
  const [volume, setVolume] = useState<number | ''>(row.volume ?? '');
  const [obligationDate, setObligationDate] = useState(toDateValue(row.obligationDate));
  const [startDate, setStartDate] = useState(toDateValue(row.startDateOfThisArrangement));
  const [endDate, setEndDate] = useState(toDateValue(row.endDateOfThisArrangement));

  // ✅ always show errors once user has interacted — not gated on forceErrors only
  const err = (field: string) => !!validations[field];

  const flush = (overrides: Partial<EnforceableArrangement>) =>
    ctrl.update({ ...row, ...overrides });

  return (
    <>
      {/* Owner */}
      <TD className="sticky-col">
        <input
          ref={ownerNameRef}
          className={`cell-input${err('ownerName') ? ' cell-input--error' : ''}`}
          defaultValue={row.ownerName || ''}
          aria-label="Owner name"
          disabled={isDisabled}
          onBlur={e => flush({ ownerName: e.target.value || undefined })}
        />
      </TD>

      {/* Beneficiary */}
      <TD >
        <input
          ref={beneficiaryRef}
          className={`cell-input${err('beneficiary') ? ' cell-input--error' : ''}`}
          defaultValue={row.beneficiary || ''}
          aria-label="Beneficiary"
          disabled={isDisabled}
          onBlur={e => flush({ beneficiary: e.target.value || undefined })}
        />
      </TD>

      {/* MSO Product — select fires on change (no blur needed, it's a single action) */}
      <TD>
        <select
          ref={msoProductIdRef}
          className={`cell-select${err('msoProductId') ? ' cell-input--error' : ''}`}
          value={msoProductId}
          disabled={isDisabled}
          aria-label="MSO Product"
          aria-invalid={err('msoProductId')}
          onChange={e => {
            const val = e.target.value === '' ? '' : Number(e.target.value);
            setMsoProductId(val);
            flush({ msoProductId: val === '' ? undefined : val as number });
          }}
        >
          <option value="">Select</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </TD>

      {/* Agreement Type */}
      <TD>
        <select
          ref={agreementTypeRef}
          className={`cell-select${err('stockOwnershipAgreementType') ? ' cell-input--error' : ''}`}
          value={stockOwnershipAgreementType}
          disabled={isDisabled}
          aria-label="Agreement type"
          aria-invalid={err('stockOwnershipAgreementType')}
          onChange={e => {
            const val = e.target.value === '' ? '' : Number(e.target.value);
            setStockOwnershipAgreementType(val);
            flush({ stockOwnershipAgreementType: val === '' ? undefined : val as number });
          }}
        >
          <option value="">Select</option>
          {AGREEMENT_TYPE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </TD>

      {/* Volume */}
      <TD>
        <input
          ref={volumeRef}
          className={`cell-input${err('volume') ? ' cell-input--error' : ''}`}
          type="number"
          value={volume}
          disabled={isDisabled}
          aria-label="Volume (ML)"
          aria-invalid={err('volume')}
          onChange={e => setVolume(e.target.value === '' ? '' : Number(e.target.value))}
          onBlur={e => flush({ volume: e.target.value === '' ? undefined : Number(e.target.value) })}
        />
      </TD>

      {/* Obligation Date */}
      <TD>
        <input
          ref={obligationDateRef}
          className={`cell-input${err('obligationDate') ? ' cell-input--error' : ''}`}
          type="date"
          value={obligationDate}
          disabled={isDisabled}
          aria-label="Obligation date"
          aria-invalid={err('obligationDate')}
          onChange={e => setObligationDate(e.target.value)}
          onBlur={e => flush({ obligationDate: e.target.value || null })}
        />
      </TD>

      {/* Start Date */}
      <TD>
        <input
          ref={startDateRef}
          className={`cell-input${err('startDateOfThisArrangement') ? ' cell-input--error' : ''}`}
          type="date"
          value={startDate}
          disabled={isDisabled}
          aria-label="Start date"
          aria-invalid={err('startDateOfThisArrangement')}
          onChange={e => setStartDate(e.target.value)}
          onBlur={e => flush({ startDateOfThisArrangement: e.target.value || null })}
        />
      </TD>

      {/* End Date */}
      <TD>
        <input
          ref={endDateRef}
          className={`cell-input${err('endDateOfThisArrangement') ? ' cell-input--error' : ''}`}
          type="date"
          value={endDate}
          disabled={isDisabled}
          aria-label="End date"
          aria-invalid={err('endDateOfThisArrangement')}
          onChange={e => setEndDate(e.target.value)}
          onBlur={e => flush({ endDateOfThisArrangement: e.target.value || null })}
        />
      </TD>

      {/* Delete */}
      <TD>
        {!isDisabled && (
          <IconButton
            icon="trash"
            color="dark-grey"
            label="Delete arrangement"
            size="sm"
            onClick={() => {
              const isNew = !row.id;  // adjust if your type uses a different "has been saved" flag
              if (isNew) {
                ctrl.remove(row);
              } else {
                deleteCtrl.requestConfirmation(
                  'Are you sure you wish to delete this data?',
                  'Arrangement will be deleted',
                  () => ctrl.remove(row)
                );
              }
            }}
          />
        )}
      </TD>
    </>
  );
};

export default EnforceableArrangementsEditor;